// src/components/ui/index.js
// The few building blocks every page shares, so pages stop reinventing headers, cards and status chips.
import React, { useEffect } from "react";
import { Button, Dropdown, Input, Modal, Popconfirm, Space, Tooltip } from "antd";
import {
  FiCheck,
  FiClock,
  FiEdit2,
  FiEye,
  FiFileText,
  FiGrid,
  FiList,
  FiMoreHorizontal,
  FiShield,
  FiSlash,
  FiTrash2,
  FiUserMinus,
  FiUserPlus,
  FiX,
} from "react-icons/fi";
import { IconRefresh, IconSearch } from "./icons";
import "./hms-ui.css";

export { fmtDate, fmtDateTime, fmtMoney } from "./format";

/** The one icon set for row actions (same stroke, same size everywhere). Pass the key as `icon`. */
export const ACTION_ICONS = {
  view: <FiEye />,
  edit: <FiEdit2 />,
  delete: <FiTrash2 />,
  document: <FiFileText />,
  history: <FiClock />,
  accept: <FiCheck />,
  reject: <FiX />,
  close: <FiSlash />,
  reserve: <FiGrid />,
  assign: <FiUserPlus />,
  unassign: <FiUserMinus />,
  checklist: <FiList />,
  permissions: <FiShield />,
};
const resolveIcon = (icon) => (typeof icon === "string" ? ACTION_ICONS[icon] : icon);

/** Call once at the top of a page that uses the shared style (drops the old white wrapper card). */
export const useFlatPage = () => {
  useEffect(() => {
    document.body.classList.add("hms-flat-page");
    return () => document.body.classList.remove("hms-flat-page");
  }, []);
};

/** Actions row at the top of a page. No title by default: the top bar already shows it. */
export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="hms-page-header">
    {(title || subtitle) && (
      <div>
        {title && <h1 className="hms-page-title">{title}</h1>}
        {subtitle && <p className="hms-page-sub">{subtitle}</p>}
      </div>
    )}
    {actions && <div className="hms-actions">{actions}</div>}
  </div>
);

/** Flat white card with an optional header row. Never nest one inside another. */
export const SectionCard = ({ title, extra, flush = false, className = "", children }) => (
  <section className={`hms-card ${flush ? "is-flush" : ""} ${className}`.trim()}>
    {(title || extra) && (
      <div className="hms-card-head">
        <span>{title}</span>
        {extra}
      </div>
    )}
    {flush ? children : title || extra ? <div className="hms-card-body">{children}</div> : children}
  </section>
);

const STATUS_CLASS = {
  confirmed: "is-confirmed",
  checkedin: "is-checkedin",
  checkedout: "is-checkedout",
  pending: "is-pending",
  unallocated: "is-unallocated",
  cancelled: "is-cancelled",
  canceled: "is-cancelled",
  noshow: "is-noshow",
  roomclosure: "is-closure",
  closure: "is-closure",
  available: "is-available",
  occupied: "is-occupied",
  reserved: "is-confirmed",
  inprogress: "is-cleaning",
  completed: "is-available",
  low: "is-available",
  high: "is-noshow",
  urgent: "is-cancelled",
  emergency: "is-cancelled",
  maintenance: "is-maintenance",
  undermaintenance: "is-maintenance",
  cleaning: "is-cleaning",
  dirty: "is-noshow",
  outofservice: "is-cancelled",
};

/** One status chip for the whole app: <StatusTag status="CheckedIn" /> */
export const StatusTag = ({ status, children }) => {
  const key = String(status ?? "").toLowerCase().replace(/[\s_-]+/g, "");
  return <span className={`hms-status ${STATUS_CLASS[key] || ""}`}>{children ?? status}</span>;
};

export const EmptyState = ({ children = "Nothing to show" }) => <div className="hms-empty">{children}</div>;

/**
 * One row-action pattern for every table.
 * actions: [{ key, label, icon (ACTION_ICONS key), onClick, danger, tone: "primary", disabled, disabledReason, hidden, confirm, okText, showLabel }]
 * more:    overflow menu items (rare, destructive actions): same shape, plus confirmDetail
 */
export const RowActions = ({ actions = [], more = [] }) => {
  // `keepSpace` keeps an empty slot for a hidden action so the icons stay in the same column on every row.
  const visible = actions.filter((a) => a && (!a.hidden || a.keepSpace));
  const overflow = more.filter((a) => a && !a.hidden);

  return (
    <div className="hms-row-actions">
      {visible.map((a) => {
        if (a.hidden) return <span key={a.key} className="hms-row-action-spacer" aria-hidden="true" />;
        const btn = (
          <Button
            type="text"
            className={`hms-row-action ${a.danger ? "is-danger" : ""} ${a.showLabel ? "has-label" : ""} ${
              a.tone === "primary" ? "is-tonal" : ""
            }`
              .replace(/\s+/g, " ")
              .trim()}
            icon={resolveIcon(a.icon)}
            disabled={a.disabled}
            aria-label={a.label}
            onClick={a.confirm ? undefined : a.onClick}
          >
            {a.showLabel ? a.label : null}
          </Button>
        );

        const body = a.confirm ? (
          <Popconfirm
            title={a.confirm}
            okText={a.okText || "Yes"}
            cancelText="No"
            disabled={a.disabled}
            onConfirm={a.onClick}
          >
            {btn}
          </Popconfirm>
        ) : (
          btn
        );

        return (
          <Tooltip key={a.key} title={a.disabled && a.disabledReason ? a.disabledReason : a.label}>
            <span className="hms-row-action-wrap">{body}</span>
          </Tooltip>
        );
      })}

      {overflow.length > 0 && (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: overflow.map((o) => ({ key: o.key, label: o.label, danger: o.danger, disabled: o.disabled })),
            onClick: ({ key }) => {
              const o = overflow.find((x) => x.key === key);
              if (!o) return;
              if (o.confirm) {
                Modal.confirm({
                  title: o.confirm,
                  content: o.confirmDetail,
                  okText: o.okText || "Confirm",
                  okButtonProps: { danger: !!o.danger },
                  cancelText: "No",
                  onOk: o.onClick,
                });
              } else if (o.onClick) {
                o.onClick();
              }
            },
          }}
        >
          <Button type="text" className="hms-row-action" icon={<FiMoreHorizontal />} aria-label="More actions" />
        </Dropdown>
      )}
    </div>
  );
};

/** Flat tab bar with neutral count pills. tabs: [{ key, label, count? }] */
export const TabBar = ({ tabs, value, onChange }) => (
  <div className="hms-tabs" role="tablist">
    {tabs.map((tab) => (
      <button
        key={tab.key}
        type="button"
        role="tab"
        aria-selected={value === tab.key}
        className={`hms-tab ${value === tab.key ? "is-active" : ""}`}
        onClick={() => onChange(tab.key)}
      >
        {tab.label}
        {tab.count != null && <span className="hms-tab-count">{tab.count}</span>}
      </button>
    ))}
  </div>
);

/** Label / value rows for drawers and dialogs (replaces bordered Descriptions). items: [{ label, value }] */
export const DetailList = ({ items }) => (
  <dl className="hms-details">
    {items
      .filter(Boolean)
      .map((it) => (
        <div className="hms-detail" key={it.label}>
          <dt>{it.label}</dt>
          <dd>{it.value === undefined || it.value === null || it.value === "" ? "—" : it.value}</dd>
        </div>
      ))}
  </dl>
);

/** A titled block inside a drawer or dialog. */
export const Section = ({ title, extra, children }) => (
  <section className="hms-dsection">
    {(title || extra) && (
      <div className="hms-dsection-head">
        <h4>{title}</h4>
        {extra}
      </div>
    )}
    {children}
  </section>
);

/** Money lines. rows: [{ label, value, strong, muted, danger }] or { divider: true } */
export const AmountRows = ({ rows }) => (
  <div className="hms-amounts">
    {rows.filter(Boolean).map((r, i) =>
      r.divider ? (
        <div key={`d${i}`} className="hms-amount-divider" />
      ) : (
        <div
          key={r.label}
          className={`hms-amount-row ${r.strong ? "is-strong" : ""} ${r.muted ? "is-muted" : ""} ${
            r.danger ? "is-danger" : ""
          }`
            .replace(/\s+/g, " ")
            .trim()}
        >
          <span>{r.label}</span>
          <span>{r.value}</span>
        </div>
      )
    )}
  </div>
);

/** One pagination for every list: 10 per page, a size switcher and "N records". Controlled props go in `extra`. */
export const tablePagination = (noun = "record", extra = {}) => ({
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: [10, 20, 50],
  showTotal: (total) => `${total} ${noun}${total === 1 ? "" : "s"}`,
  ...extra,
});

/**
 * The toolbar that sits in a SectionCard header (`extra`): filters (children), search, refresh. Same on every list.
 * <ListToolbar search={{ value, onChange, placeholder }} onRefresh={load} loading={loading}>{filters}</ListToolbar>
 */
export const ListToolbar = ({ search, onRefresh, loading, children }) => (
  <Space wrap size={8}>
    {children}
    {search && (
      <Input
        allowClear
        style={{ width: 260 }}
        prefix={<IconSearch />}
        placeholder={search.placeholder || "Search"}
        value={search.value}
        onChange={(e) => search.onChange(e.target.value)}
      />
    )}
    {onRefresh && (
      <Button icon={<IconRefresh />} onClick={onRefresh} loading={loading}>
        Refresh
      </Button>
    )}
  </Space>
);

/** "Card title" with the neutral count pill: <CardTitle count={12}>Room types</CardTitle> */
export const CardTitle = ({ count, children }) => (
  <>
    {children}
    {count !== undefined && count !== null && <span className="hms-count">{count}</span>}
  </>
);

/** A row of KPI numbers inside one card. items: [{ label, value, suffix, tone: "good" | "warn" | "bad" }] */
export const StatStrip = ({ items }) => (
  <div className="hms-stats">
    {items.map((it) => (
      <div className={`hms-stat ${it.tone ? `is-${it.tone}` : ""}`.trim()} key={it.label}>
        <div className="hms-stat-label">{it.label}</div>
        <div className="hms-stat-value">
          {it.value}
          {it.suffix && <span className="hms-stat-suffix">{it.suffix}</span>}
        </div>
      </div>
    ))}
  </div>
);

/** Honest placeholder for modules that are in the menu but not built yet. */
export const ComingSoon = ({ title, description }) => {
  useFlatPage();
  return (
    <div className="hms-page">
      <SectionCard>
        <div className="hms-summary" style={{ marginBottom: 0 }}>
          <div>
            <div className="hms-summary-title">{title}</div>
            <div className="hms-summary-sub">{description || "This page is not available yet."}</div>
          </div>
          <StatusTag status="pending">Coming soon</StatusTag>
        </div>
      </SectionCard>
    </div>
  );
};
